<template>
  <div
    v-if="isVisible"
    data-slot="fullscreen-modal"
    class="fixed inset-0 z-50"
    role="dialog"
    aria-modal="true"
    @click="() => onClickBackground()"
  >
    <div :class="['blur-overlay absolute inset-0 transition-opacity duration-150', isTransitioning ? 'opacity-0' : 'opacity-100']" />

    <ui-button
      v-if="closable"
      variant="glass"
      size="icon"
      :aria-label="tr('Close')"
      :class="['fixed top-5 right-5 z-10 rounded-full transition-opacity', isClosing && 'pointer-events-none opacity-0']"
      @click.stop="() => close()"
    >
      <x-icon />
    </ui-button>

    <div
      :class="[
        'absolute inset-y-0 left-1/2 w-full -translate-x-1/2 transition-[opacity,translate] duration-150',
        wide ? 'max-w-[1040px]' : 'max-w-[490px]',
        center && 'flex items-center',
        isTransitioning && '-translate-y-2 opacity-0',
      ]"
      @click.stop
    >
      <div class="scrollbar-thin relative m-auto flex h-full w-full flex-col overflow-auto px-4 pt-5">
        <component
          :is="component"
          v-bind="resolvedProperties"
          v-on="componentListeners"
          @close="() => close()"
          @close-all="() => closeAll()"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { X } from '@lucide/vue';

/**
 * One entry of the $fullscreenModal stack: a modal content component
 * (built on <ui-modal-layout>) on a blurred full-screen backdrop.
 */
export default {
  name: 'FullscreenModal',
  components: {
    XIcon: X,
  },
  props: {
    component: {
      type: Object,
      required: true,
    },
    properties: {
      type: [ Object, Function ],
      required: false,
      default: () => ({}),
    },
    // Wide frame for tool-like contents (e.g. the communicator debugger).
    wide: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    center: {
      type: Boolean,
      required: false,
      default: () => true,
    },
    closable: {
      type: Boolean,
      required: false,
      default: () => true,
    },
    closeWithOutclick: {
      type: Boolean,
      required: false,
      default: () => true,
    },
    componentListeners: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    modalHandle: {
      type: Object,
      required: false,
      default: () => null,
    },
  },
  data: () => ({
    isVisible: false,
    isOpening: false,
    isClosing: false,
  }),
  computed: {
    isTransitioning() {
      return this.isOpening || this.isClosing;
    },
    resolvedProperties() {
      if (typeof this.properties === 'function') {
        return this.properties();
      }
      return this.properties;
    },
  },
  mounted() {
    if (this.modalHandle) {
      this.modalHandle._vm = this;
    }

    this.open();
  },
  methods: {
    open() {
      this.isOpening = true;
      this.isVisible = true;
      setTimeout(() => this.isOpening = false, 10);

      document.body.classList.add('overflow-hidden');
    },
    close() {
      this.isClosing = true;

      if (this.modalHandle) {
        this.modalHandle.$emit('closing');
      }

      setTimeout(() => {
        this.isVisible = false;
        this.isClosing = false;

        this.$fullscreenModal.closed(this.modalHandle);
      }, 300);
    },
    closeAll() {
      this.$fullscreenModal.closeAll();
    },
    onClickBackground() {
      if (this.closable && this.closeWithOutclick) {
        this.close();
      }
    },
  },
};
</script>
